//@ts-nocheck
import "chart.js/auto";
import { ChartOptions } from "chart.js";
import currency from "currency.js";
import { SPACING } from "../../../constants/design/spacing";
import { cssColor } from "../../../utils/themeColor";
import { getMidPrice } from "../../../utils/orderbook";
import { IDepthData } from "./types";

export const upColor = (a?: number) => cssColor("--color-up", "22 199 132", a);
export const downColor = (a?: number) => cssColor("--color-down", "234 57 67", a);

export const MAX_ZOOM = 0.5;

/**
 * bids and asks are both sorted asc by price
 */
export function computeSortedBidAsks(bids, asks, zoom) {
  const maxLength =
    Math.floor(Math.min(bids.length, asks.length) * (1 - zoom)) || 0;

  let sortedBids = bids.sort((a, b) => Number(a[0]) - Number(b[0]));

  // Cumulative size and total costs
  sortedBids = sortedBids
    .reverse()
    .reduce((arr, [price, size, totalCost], index) => {
      const prevSize =
        arr.length && index > 0 ? Number(arr[index - 1][1]) || 0 : 0;
      const currentSize = String(Number(size) + Number(prevSize));
      const currentTotalCosts =
        Number(totalCost) || Number(price) * Number(size);
      const prevTotalCost =
        arr.length && index > 0
          ? // If no prev total cost, calculate by multiplying price and size
            Number(arr[index - 1][2]) || 0
          : 0;
      const currentTotalCost = String(currentTotalCosts + prevTotalCost);
      return [...arr, [price, currentSize, currentTotalCost]];
    }, [] as IDepthData)
    .reverse();

  let sortedAsks = asks.sort((a, b) => Number(a[0]) - Number(b[0]));

  // Cumulative size and total costs
  sortedAsks = sortedAsks.reduce((arr, [price, size, totalCost], index) => {
    const prevSize =
      arr.length && index > 0 ? Number(arr[index - 1][1]) || 0 : 0;
    const currentSize = String(Number(size) + Number(prevSize));
    const currentTotalCosts =
      Number(totalCost) || Number(price) * Number(size);
    const prevTotalCost =
      arr.length && index > 0
        ? // If no prev total cost, calculate by multiplying price and size
          Number(arr[index - 1][2]) || 0
        : 0;
    const currentTotalCost = String(currentTotalCosts + prevTotalCost);
    return [...arr, [price, currentSize, currentTotalCost]];
  }, [] as IDepthData);

  sortedBids = sortedBids.slice(sortedBids.length - maxLength);
  sortedAsks = sortedAsks.slice(0, maxLength);

  // `undefined` for a one-sided or empty book: the header shows "-", not $0.00.
  const midPrice = getMidPrice(sortedBids, sortedAsks);
  return {
    sortedBids,
    sortedAsks,
    midPrice,
  };
}

export function computeLabelsAndDatas(sortedBidAsks, hoveredIndex) {
  const { sortedBids, sortedAsks, midPrice } = sortedBidAsks;
  // The chart still needs an x-position between the sides; with no mid (a
  // one-sided book) it sits at the touch that exists. Axis anchor only — the
  // header's "Mid-Market Price" reads `midPrice` itself.
  const centre =
    midPrice ??
    Number(sortedAsks[0]?.[0] ?? sortedBids[sortedBids.length - 1]?.[0]);
  const labels = [
    ...sortedBids.map((v) => Number(v[0])),
    centre,
    ...sortedAsks.map((v) => Number(v[0])),
  ];

  const bidsData: number[] = [
    ...sortedBids.map((v) => Number(v[1])),
    0,
    ...Array(sortedAsks.length).fill(null),
  ];
  const asksData: number[] = [
    ...Array(sortedBids.length).fill(null),
    0,
    ...sortedAsks.map((v) => Number(v[1])),
  ];
  const bidsTotalValueData: number[] = [
    ...sortedBids.map((v) => Number(v[2])),
    0,
    ...Array(sortedAsks.length).fill(null),
  ];
  const asksTotalValueData: number[] = [
    ...Array(sortedBids.length).fill(null),
    0,
    ...sortedAsks.map((v) => Number(v[2])),
  ];
  const hoveredData = Array(bidsData.length).fill(null);
  const hoveredTotalCostsData = Array(bidsData.length).fill(null);

  if (hoveredIndex?.bidIndex || hoveredIndex?.bidIndex === 0) {
    hoveredData[hoveredIndex.bidIndex] = bidsData[hoveredIndex.bidIndex];
    hoveredTotalCostsData[hoveredIndex.bidIndex] =
      bidsTotalValueData[hoveredIndex.bidIndex];
  }
  if (hoveredIndex?.askIndex || hoveredIndex?.askIndex === 0) {
    hoveredData[hoveredIndex.askIndex] = asksData[hoveredIndex.askIndex];
    hoveredTotalCostsData[hoveredIndex.askIndex] =
      asksTotalValueData[hoveredIndex.askIndex];
  }

  return {
    // labels is an array of PRICE
    labels,
    // bids/asks data is an array of SIZE
    bidsData,
    asksData,
    // hovered data is an array of SIZE at the hovered index
    hoveredData,
    hoveredTotalCostsData,
  };
}

export function computeScaleRanges(sortedBidAsks) {
  const { sortedBids, sortedAsks } = sortedBidAsks;

  const minX = Number(sortedBids[0]?.[0]);
  const maxX = Number(sortedAsks[sortedAsks.length - 1]?.[0]);

  const validBids = sortedBids.filter((v) => Number(v[0]) >= minX);
  const validAsks = sortedAsks.filter((v) => Number(v[0]) <= maxX);

  const volumes = [
    ...validBids.map((v) => Number(v[1])),
    ...validAsks.map((v) => Number(v[1])),
  ];

  return {
    minX,
    maxX,
    minY: Math.min(...volumes),
    maxY: Math.max(...volumes),
  };
}

export function getChartOptions(
  scaleRanges,
  precision,
  sortedBidAsks,
  setHoveredIndex
): ChartOptions<"line"> {
  return {
    maintainAspectRatio: false,
    responsive: true,
    animation: false,
    layout: {
      padding: {
        right: SPACING.two,
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    scales: {
      y: {
        display: true,
        position: "right",
        min: scaleRanges.minY,
        max: scaleRanges.maxY,
        ticks: {
          maxTicksLimit: 5,
          font: { family: "BaseFont", size: 13 },
          callback(value) {
            return Number(value).toFixed(precision().amount_precision) || "";
          },
        },
        grid: { color: "transparent" },
      },
      x: {
        type: "category",
        display: true,
        ticks: {
          maxTicksLimit: 5,
          maxRotation: 0,
          font: { family: "BaseFont", size: 13 },
          callback(value) {
            // In category scale, value is the index; get the actual label
            const label = this.getLabelForValue(value as number);
            return currency(Number(label), {
              precision: precision().price_precision,
            }).format();
          },
        },
        grid: { color: "transparent" },
      },
    },
    interaction: { intersect: false, mode: "index" },
    onHover: (_: any, elements: any) => {
      if (elements && elements.length) {
        // v3: element.index (not ._index)
        const dataIndex = elements[0].index;
        const { sortedBids } = sortedBidAsks;
        const middleIndex = sortedBids.length;

        // Don't hover on middle point (zero crossover)
        if (dataIndex === middleIndex) {
          return;
        }

        let bidIndex: number | undefined = dataIndex;
        let askIndex: number | undefined;
        if (dataIndex > middleIndex) {
          bidIndex = undefined;
          askIndex = dataIndex;
        }

        setHoveredIndex({ bidIndex, askIndex });
        return;
      }

      setHoveredIndex(undefined);
    },
  };
}
